/** Presentation helpers. Pure, no DOM, no state. */

import type { PlaybackState, PresenceState, QueueEntryStatus } from '../generated';

export function formatDuration(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.round(totalMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number): string => value.toString().padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

export function formatPercent(fraction: number): string {
  return `${Math.round(Math.min(Math.max(fraction, 0), 1) * 100)}%`;
}

/** Signed millisecond value for drift readouts, e.g. `+42 ms`. */
export function formatSignedMs(value: number): string {
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded} ms`;
}

export function presenceLabel(presence: PresenceState): string {
  switch (presence) {
    case 'joining':
      return 'joining';
    case 'autoplay-blocked':
      return 'needs to press play';
    case 'ready':
      return 'ready';
    case 'playing':
      return 'watching';
    case 'buffering':
      return 'buffering';
    case 'disconnected':
      return 'disconnected';
  }
}

export function queueStatusLabel(status: QueueEntryStatus): string {
  switch (status.state) {
    case 'queued':
      return 'queued';
    case 'resolving':
      return 'looking up details…';
    case 'preparing':
      return status.progress === null
        ? 'preparing on the server…'
        : `preparing ${formatPercent(status.progress)}`;
    case 'ready':
      return `ready · ${formatDuration(status.duration_ms)}`;
    case 'failed':
      return `failed: ${status.message}`;
  }
}

export function playbackLabel(playback: PlaybackState): string {
  switch (playback.state) {
    case 'idle':
      return 'Nothing queued';
    case 'preparing':
      return 'Preparing the next video…';
    case 'playing':
      return 'Playing';
    case 'paused':
      return 'Paused';
    case 'buffering':
      return 'Waiting for viewers to buffer';
    case 'failed':
      return 'This video failed';
  }
}
